<template>
  <ModalWrapper
    ref="modal"
    :title="actionType === 'Approved' ? 'Approve Leave Request' : 'Reject Leave Request'"
    size="medium"
  >
    <template #body>
      <div v-if="leaveRequest.names" class="approval-preview hr-mb-3">
        <div class="hr-flex hr-flex-center hr-gap-3">
          <div class="avatar-initials-large">{{ getInitials(leaveRequest.names) }}</div>
          <div>
            <h6 class="approval-name">{{ leaveRequest.names }}</h6>
            <p class="approval-meta">{{ leaveRequest.leaveTypeName }} - {{ leaveRequest.totalDays }} Days</p>
            <p class="approval-meta">{{ formatDate(leaveRequest.startDate) }} to {{ formatDate(leaveRequest.endDate) }}</p>
          </div>
        </div>
      </div>

      <p class="approval-question hr-mb-3">
        Are you sure you want to <strong :class="actionType === 'Approved' ? 'text-success' : 'text-danger'">
          {{ actionType.toLowerCase() }}
        </strong> this leave request?
      </p>

      <div class="hr-mb-3">
        <label class="hr-form-label">
          Remarks <span class="hr-text-muted">({{ actionType === 'Rejected' ? 'Required' : 'Optional' }})</span>
        </label>
        <textarea
          v-model="remarks"
          class="hr-textarea"
          rows="4"
          :placeholder="actionType === 'Approved' ? 'Add any comments or conditions (optional)...' : 'Please provide reason for rejection...'"
        ></textarea>
        <div v-if="actionType === 'Rejected' && showError" class="text-error small">Please provide a reason for rejection</div>
      </div>
    </template>
    <template #footer>
      <button type="button" class="hr-btn hr-btn-secondary" @click="closeModal">Cancel</button>
      <button
        type="button"
        :class="['hr-btn', actionType === 'Approved' ? 'hr-btn-success' : 'hr-btn-danger']"
        @click="confirmAction"
        :disabled="processing"
      >
        <span v-if="processing" class="hr-spinner-inline"></span>
        <i v-else :class="['bx', actionType === 'Approved' ? 'bx-check' : 'bx-x']" class="hr-me-2"></i>
        {{ processing ? 'Processing...' : `Confirm ${actionType}` }}
      </button>
    </template>
  </ModalWrapper>
</template>

<script>
import ModalWrapper from "@/components/hrActions/ModalWrapper.vue";
import "@/assets/css/hr-page-shared.css";

export default {
  name: "ApprovalConfirmationModal",
  components: { ModalWrapper },
  props: {
    leaveRequest: { type: Object, default: () => ({}) },
    actionType: { type: String, default: 'Approved' }
  },
  data() {
    return {
      remarks: '',
      processing: false,
      showError: false
    };
  },
  watch: {
    leaveRequest: {
      handler() {
        this.remarks = '';
        this.showError = false;
        this.processing = false;
      },
      deep: true
    },
    actionType() {
      this.remarks = '';
      this.showError = false;
      this.processing = false;
    }
  },
  methods: {
    open() {
      this.$refs.modal?.open();
    },
    close() {
      this.$refs.modal?.close();
    },
    getInitials(name) {
      if (!name) return 'NA';
      return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    },
    formatDate(date) {
      if (!date) return '-';
      const d = new Date(date);
      return d.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
    },
    confirmAction(event) {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      if (this.actionType === 'Rejected' && !this.remarks.trim()) {
        this.showError = true;
        return;
      }
      this.showError = false;
      this.processing = true;
      this.$emit('confirm', {
        leaveRequest: this.leaveRequest,
        action: this.actionType,
        remarks: this.remarks.trim()
      });
    },
    closeModal() {
      this.remarks = '';
      this.processing = false;
      this.showError = false;
      this.close();
    },
    resetProcessing() {
      this.processing = false;
    }
  }
};
</script>

<style scoped>
.avatar-initials-large {
  width: 48px;
  height: 48px;
  min-width: 48px;
  border-radius: 12px;
  background: #dbeafe;
  color: #3b82f6;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 600;
}

.approval-preview {
  padding: 16px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
}

.approval-name {
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 4px 0;
}

.approval-meta {
  font-size: 13px;
  color: #64748b;
  margin: 0;
}

.approval-question {
  font-size: 14px;
  color: #475569;
}

.text-success { color: #10b981; }
.text-danger { color: #ef4444; }
.text-error { color: #ef4444; margin-top: 6px; font-size: 13px; }

.hr-spinner-inline {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid #e2e8f0;
  border-top-color: #059669;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  vertical-align: middle;
  margin-right: 8px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>
